import { CRM_BFF_URL } from './runtimeEnv.js';

// Client for the public CRM edge (parsina.bff.crm, CRM_MODE=public).
// Contract: POST /public/v1/leads — see marketing/parsina.bff.crm.
//
// Success is ALWAYS 202 {ok, status:'accepted', reference}: the edge
// deliberately answers a duplicate byte-identically, so this page never knows
// (and never shows) whether a number was already in the CRM.

export class SubmitError extends Error {
  constructor(code, message, { fields, retryAfterSeconds } = {}) {
    super(message);
    this.code = code; // VALIDATION_FAILED | FORM_TOKEN_* | RATE_LIMITED | SERVICE_UNAVAILABLE | NETWORK
    this.fields = fields || null; // {fieldName: message} from the edge
    this.retryAfterSeconds = retryAfterSeconds || null;
  }
}

const MESSAGES = {
  NETWORK: 'اتصال برقرار نشد. اینترنت خود را بررسی کنید و دوباره تلاش کنید.',
  VALIDATION_FAILED: 'اطلاعات ارسال‌شده معتبر نیست. لطفاً فرم را بررسی کنید.',
  RATE_LIMITED: 'تعداد درخواست‌ها بیش از حد مجاز است. لطفاً چند دقیقه دیگر دوباره تلاش کنید.',
  SERVICE_UNAVAILABLE: 'ثبت درخواست در حال حاضر ممکن نیست. لطفاً دقایقی دیگر دوباره تلاش کنید.',
  FORM_TOKEN: 'مهلت فرم به پایان رسید. لطفاً یک‌بار دیگر روی دکمهٔ ثبت بزنید.',
};

async function parseJson(res) {
  try {
    return await res.json();
  } catch {
    return null;
  }
}

// → { token, minAgeMs } — fetched on form render; the token must age >=2s
// before the edge accepts it (the anti-bot control).
export async function fetchFormToken({ fetchImpl = fetch, baseUrl = CRM_BFF_URL } = {}) {
  let res;
  try {
    res = await fetchImpl(`${baseUrl}/public/v1/form-token`);
  } catch {
    // A bare fetch rejection (edge down, DNS, or a CORS-blocked error response)
    // is a TypeError whose English message would otherwise reach the visitor.
    throw new SubmitError('NETWORK', MESSAGES.NETWORK);
  }
  if (!res.ok) throw new SubmitError('SERVICE_UNAVAILABLE', MESSAGES.SERVICE_UNAVAILABLE);
  const body = await parseJson(res);
  if (!body?.token) throw new SubmitError('SERVICE_UNAVAILABLE', MESSAGES.SERVICE_UNAVAILABLE);
  return { token: body.token, minAgeMs: body.minAgeMs ?? 2000 };
}

export function isFormTokenError(err) {
  return typeof err?.code === 'string' && err.code.startsWith('FORM_TOKEN_');
}

// payload keys must stay inside the edge's strict allowlist — an unknown key
// is a 400, not ignored. → { reference }
export async function submitLead(payload, { fetchImpl = fetch, baseUrl = CRM_BFF_URL } = {}) {
  let res;
  try {
    res = await fetchImpl(`${baseUrl}/public/v1/leads`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
  } catch {
    throw new SubmitError('NETWORK', MESSAGES.NETWORK);
  }

  const body = await parseJson(res);

  if (res.status === 202 && body?.ok) {
    // `gift` is present on every accepted submission — see the gateway's
    // single-202 rule. Absent only when the campaign's gift is switched off.
    return { reference: body.reference, gift: body.gift || null };
  }

  const code =
    body?.error?.code || (res.status === 429 ? 'RATE_LIMITED' : 'SERVICE_UNAVAILABLE');

  if (code.startsWith('FORM_TOKEN_')) {
    throw new SubmitError(code, MESSAGES.FORM_TOKEN);
  }
  if (code === 'VALIDATION_FAILED') {
    throw new SubmitError(code, MESSAGES.VALIDATION_FAILED, { fields: body?.error?.fields });
  }
  if (code === 'RATE_LIMITED') {
    throw new SubmitError(code, MESSAGES.RATE_LIMITED, {
      retryAfterSeconds: body?.error?.retryAfterSeconds,
    });
  }
  throw new SubmitError('SERVICE_UNAVAILABLE', MESSAGES.SERVICE_UNAVAILABLE);
}
